import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { Category, Product } from '@core/db/models'
import { cx, hint, table, tableWrap, td, tdNum, th, thNum } from '@ui/classes'
import { money } from '@core/utils/format'
import {
  badgeNo,
  badgePosCreated,
  badgeWeight,
  badgeYes,
  credInput,
  errBanner,
  filterBar,
  filterChip,
  okBanner,
  priceInput
} from '@ui/admin'
import { Marking, Scales } from '@ui/icons'
import { syncService } from '@core/services/sync/SyncService'
import { hasPendingEdit } from '@core/services/catalog/pendingProductEdits'
import { useAuthToken } from './useAuthToken'

/** Классы инлайн-полей таблицы каталога. */
const nameInput =
  'w-full min-w-[170px] rounded-md border border-lineStrong px-1.5 py-1 text-small text-ink'
const barcodeInput =
  'w-[130px] rounded-md border border-lineStrong px-1.5 py-1 font-mono text-tiny text-ink'
const categorySelect =
  'min-w-[190px] max-w-[260px] rounded-md border border-lineStrong px-1.5 py-1 text-small text-ink'

/** Опция фильтра/селектора категории: верхний уровень или конкретный лист. */
interface CategoryChoice {
  /** Значение для select (индекс в списке). */
  value: string
  label: string
  parent: string
  /** null — вся верхняя категория. */
  leaf: string | null
}

/**
 * Таблица товаров активной версии с клиентскими фильтрами и правкой полей.
 *
 * Редактируются наименование, категория, штрихкод, маркировка, цена и
 * минимальный остаток. Изменения применяются сразу локально и уходят в
 * облако очередью правок (не затираются дельтой каталога).
 */
export function ActiveCatalogSection({
  core,
  refreshKey
}: {
  core: AppCore
  refreshKey: number
}) {
  const { catalog } = core
  const token = useAuthToken()
  const [products, setProducts] = useState<Product[]>([])
  const [tree, setTree] = useState<Category[]>([])
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Фильтры таблицы каталога (клиентская фильтрация без перезагрузки).
  const [filterText, setFilterText] = useState('')
  const [filterChoice, setFilterChoice] = useState('all')
  const [filterWeight, setFilterWeight] = useState(false)
  const [filterMarked, setFilterMarked] = useState(false)
  const [filterOnPos, setFilterOnPos] = useState(false)
  /** Общий порог магазина — подсказка в поле «Мин. остаток». */
  const [storeThreshold, setStoreThreshold] = useState(3)

  const refresh = useCallback(async () => {
    const [list, threshold, categories] = await Promise.all([
      catalog.listActiveProducts(),
      catalog.getLowStockThreshold(),
      catalog.listCategoryTree()
    ])
    setProducts(list)
    setStoreThreshold(threshold)
    setTree(categories)
  }, [catalog])

  useEffect(() => {
    void refresh().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
  }, [refresh, refreshKey])

  const parentOf = (p: Product): string =>
    p.categoryParent?.trim() || p.category?.trim() || 'Общее'
  const leafOf = (p: Product): string => p.category?.trim() || 'Общее'

  /**
   * Единый список «верхняя категория + её подкатегории». Строится из дерева
   * (включая пустые категории) и из товаров — на случай расхождений.
   */
  const choices = useMemo<CategoryChoice[]>(() => {
    const parents = new Map<string, Set<string>>()
    for (const category of tree) {
      if (category.parentName == null) {
        if (!parents.has(category.name)) parents.set(category.name, new Set())
      } else {
        if (!parents.has(category.parentName)) parents.set(category.parentName, new Set())
        parents.get(category.parentName)?.add(category.name)
      }
    }
    for (const p of products) {
      const parent = parentOf(p)
      const leaf = leafOf(p)
      if (!parents.has(parent)) parents.set(parent, new Set())
      if (leaf !== parent) parents.get(parent)?.add(leaf)
    }

    const result: CategoryChoice[] = []
    const sortedParents = [...parents.keys()].sort((a, b) => a.localeCompare(b, 'ru'))
    for (const parent of sortedParents) {
      const leaves = [...(parents.get(parent) ?? [])].sort((a, b) =>
        a.localeCompare(b, 'ru')
      )
      result.push({
        value: String(result.length),
        label: leaves.length > 0 ? `${parent} — вся категория` : parent,
        parent,
        leaf: null
      })
      for (const leaf of leaves) {
        result.push({
          value: String(result.length),
          label: `— ${leaf}`,
          parent,
          leaf
        })
      }
    }
    return result
  }, [tree, products])

  const selected = filterChoice === 'all' ? null : choices[Number(filterChoice)] ?? null

  const filteredProducts = products.filter((p) => {
    if (selected && parentOf(p) !== selected.parent) return false
    if (selected?.leaf && leafOf(p) !== selected.leaf) return false
    if (filterWeight && !(p.isWeight || p.unit === 'кг')) return false
    if (filterMarked && !p.requiresMarking) return false
    if (filterOnPos && !p.isCreatedOnPos) return false
    const q = filterText.trim().toLowerCase()
    if (q) {
      const hay = `${p.name} ${p.barcode ?? ''} ${p.price}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })

  /** Сообщение о судьбе правки: сразу в облако или в очередь при связи. */
  const reportSaved = async (
    product: Product,
    text: string,
    queued: boolean
  ): Promise<void> => {
    if (!queued) {
      setMessage(`${text} (только на этом устройстве — товар ещё не в облаке).`)
      return
    }
    if (token) await syncService.pushPendingEdits(token)
    const left = product.cloudId != null ? await hasPendingEdit(product.cloudId) : false
    setMessage(
      left
        ? `${text}. Отправим в облако при связи.`
        : text
    )
  }

  /** Инлайн-редактирование цены по onBlur. */
  const onPriceBlur = async (product: Product, raw: string) => {
    const next = Number(raw.replace(',', '.'))
    if (!Number.isFinite(next) || next < 0 || product.id == null) {
      await refresh()
      return
    }
    if (next === product.price) return
    try {
      const { queued } = await catalog.updatePrice(product.id, next)
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, price: next } : p))
      )
      await reportSaved(product, `Цена «${product.name}»: ${money(next)}`, queued)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
      await refresh()
    }
  }

  /** Правка наименования, штрихкода, категории или маркировки. */
  const saveFields = async (
    product: Product,
    patch: {
      name?: string
      barcode?: string
      category?: string
      categoryParent?: string
      requiresMarking?: boolean
    }
  ) => {
    if (product.id == null) return
    if (
      patch.name !== undefined &&
      patch.name.trim() === product.name
    ) {
      return
    }
    if (patch.barcode !== undefined && patch.barcode.trim() === (product.barcode ?? '')) {
      return
    }
    try {
      const { queued } = await catalog.updateProductFields(product.id, patch)
      await refresh()
      const label =
        patch.name !== undefined
          ? `Наименование «${patch.name.trim()}»`
          : patch.category !== undefined
            ? `Категория «${product.name}»: ${patch.categoryParent ?? patch.category}`
            : patch.barcode !== undefined
              ? `Штрихкод «${product.name}»: ${patch.barcode.trim() || 'пусто'}`
              : patch.requiresMarking
                ? `Маркировка «${product.name}»: да`
                : `Маркировка «${product.name}»: нет`
      await reportSaved(product, label, queued)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
      await refresh()
    }
  }

  /** Деактивация/активация товара: номер сохраняется, но скрывается. */
  const onActiveToggle = async (product: Product, active: boolean) => {
    if (product.id == null) return
    try {
      await catalog.setProductActive(product.id, active)
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, isActive: active } : p))
      )
      setMessage(
        active
          ? `Товар «${product.name}» активирован`
          : `Товар «${product.name}» деактивирован: с кассы скрыт, номер сохранён`
      )
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const onMinStockBlur = async (product: Product, raw: string) => {
    if (product.id == null) return
    const trimmed = raw.trim()
    const next = trimmed === '' ? null : Number(trimmed.replace(',', '.'))
    if (next != null && (!Number.isFinite(next) || next < 0)) {
      await refresh()
      return
    }
    if ((next ?? undefined) === (product.minStock ?? undefined)) return
    try {
      const { queued } = await catalog.updateMinStock(product.id, next)
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, minStock: next ?? undefined } : p))
      )
      const valueText =
        next == null ? `общий порог магазина (${storeThreshold})` : `${next} ед.`
      await reportSaved(product, `Мин. остаток «${product.name}»: ${valueText}`, queued)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
      await refresh()
    }
  }

  const hasStock = products.some((p) => Boolean(p.stock))

  return (
    <div>
      <p className="m-0 mb-2 text-small text-muted">
        Показано {filteredProducts.length} из {products.length}
      </p>

      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}

      {/* Строка 1: поиск + единый фильтр категорий. */}
      <div className={filterBar}>
        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Поиск по наименованию, штрихкоду, цене…"
          className={cx(credInput, 'flex-1 min-w-[220px]')}
        />
        <select
          value={filterChoice}
          onChange={(e) => setFilterChoice(e.target.value)}
          className={cx(credInput, 'max-w-[320px]')}
          aria-label="Фильтр по категории"
        >
          <option value="all">Все категории ({products.length})</option>
          {choices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </div>

      {/* Строка 2: быстрые флаги — всегда в один ряд. */}
      <div className="mb-3 flex items-center gap-2 overflow-x-auto pb-1">
        <label className={cx(filterChip, 'shrink-0')}>
          <input
            type="checkbox"
            checked={filterWeight}
            onChange={(e) => setFilterWeight(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-ok"
          />
          <Scales size={16} /> Только весовые
        </label>
        <label className={cx(filterChip, 'shrink-0')}>
          <input
            type="checkbox"
            checked={filterMarked}
            onChange={(e) => setFilterMarked(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-ok"
          />
          <Marking size={16} /> Только маркированные
        </label>
        <label className={cx(filterChip, 'shrink-0')}>
          <input
            type="checkbox"
            checked={filterOnPos}
            onChange={(e) => setFilterOnPos(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-ok"
          />
          Только созданные на кассе
        </label>
      </div>

      {filteredProducts.length === 0 ? (
        <p className={hint}>
          {products.length === 0
            ? 'В активной версии пока нет товаров.'
            : 'Ничего не найдено по заданным фильтрам.'}
        </p>
      ) : (
        <div className={tableWrap}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Наименование</th>
                <th className={th}>Категория</th>
                <th className={thNum}>№</th>
                <th className={th}>Штрихкод</th>
                <th className={thNum}>Цена, ₽</th>
                <th className={thNum}>Мин. остаток</th>
                {hasStock && <th className={thNum}>Остаток</th>}
                <th className={th}>Маркировка</th>
                <th className={th}>Активен</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) => (
                <tr key={p.id}>
                  <td className={td}>
                    <div className="flex flex-col gap-1">
                      <input
                        key={`name-${p.id}-${p.name}`}
                        defaultValue={p.name}
                        onBlur={(e) => void saveFields(p, { name: e.target.value })}
                        className={nameInput}
                        aria-label={`Наименование «${p.name}»`}
                      />
                      {(p.isWeight || p.unit === 'кг') && (
                        <span className={badgeWeight}>
                          <Scales size={16} /> кг
                        </span>
                      )}
                      {p.isCreatedOnPos && (
                        <span className={badgePosCreated}>Создан на кассе</span>
                      )}
                    </div>
                  </td>
                  <td className={td}>
                    <select
                      value={`${parentOf(p)}\u0000${leafOf(p)}`}
                      onChange={(e) => {
                        const [parent, leaf] = e.target.value.split('\u0000')
                        void saveFields(p, { categoryParent: parent, category: leaf })
                      }}
                      className={categorySelect}
                      aria-label={`Категория «${p.name}»`}
                    >
                      <option value={`${parentOf(p)}\u0000${leafOf(p)}`}>
                        {parentOf(p) === leafOf(p)
                          ? leafOf(p)
                          : `${parentOf(p)} / ${leafOf(p)}`}
                      </option>
                      {choices
                        .filter(
                          (choice) =>
                            !(
                              choice.parent === parentOf(p) &&
                              (choice.leaf ?? leafOf(p)) === leafOf(p)
                            )
                        )
                        .map((choice) => (
                          <option
                            key={`${p.id}-${choice.value}`}
                            value={`${choice.parent}\u0000${choice.leaf ?? choice.parent}`}
                          >
                            {choice.label}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className={tdNum}>{p.categoryOrdinal ?? '—'}</td>
                  <td className={td}>
                    <input
                      key={`barcode-${p.id}-${p.barcode ?? ''}`}
                      defaultValue={p.barcode ?? ''}
                      onBlur={(e) => void saveFields(p, { barcode: e.target.value })}
                      className={barcodeInput}
                      placeholder="—"
                      aria-label={`Штрихкод «${p.name}»`}
                    />
                  </td>
                  <td className={tdNum}>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      defaultValue={p.price}
                      onBlur={(e) => void onPriceBlur(p, e.target.value)}
                      className={priceInput}
                    />
                  </td>
                  <td className={tdNum}>
                    <input
                      type="number"
                      min={0}
                      step="0.5"
                      defaultValue={p.minStock ?? ''}
                      placeholder={String(storeThreshold)}
                      title="Пусто — общий порог магазина"
                      onBlur={(e) => void onMinStockBlur(p, e.target.value)}
                      className={priceInput}
                    />
                  </td>
                  {hasStock && <td className={tdNum}>{p.stock}</td>}
                  <td className={td}>
                    <button
                      type="button"
                      className={cx(
                        p.requiresMarking ? badgeYes : badgeNo,
                        'cursor-pointer'
                      )}
                      onClick={() =>
                        void saveFields(p, { requiresMarking: !p.requiresMarking })
                      }
                      title="Переключить обязательную маркировку"
                      aria-label={`Маркировка «${p.name}»: ${p.requiresMarking ? 'да' : 'нет'}`}
                    >
                      {p.requiresMarking ? 'Да' : 'Нет'}
                    </button>
                  </td>
                  <td className={td}>
                    <input
                      type="checkbox"
                      checked={p.isActive !== false}
                      onChange={(e) => void onActiveToggle(p, e.target.checked)}
                      className="h-4 w-4 cursor-pointer accent-ok"
                      title="Деактивация скрывает товар с кассы, номер сохраняется"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
